import {Button} from '@mantine/core';
import {AiOutlineMail} from 'react-icons/ai';
import {I18n} from '@aws-amplify/core';

import {useSendInvoicesMutation} from '../hooks/useInvoicesQuery';

interface SendRecordButtonProps {
    selectedIds: string[];
    onSendComplete?: () => void;
}

/**
 * Batch send — emails the selected invoices to their customers.
 *
 * FNP-918: takes the mutation directly rather than instantiating the whole
 * `useInvoices` list hook for one callback, and AWAITS it before clearing the
 * selection. The previous version fired `sendInvoices(ids)` and cleared on the
 * same tick, so a failed send silently dropped the operator's selection and
 * there was no way to tell which invoices actually went out.
 *
 * ⚠️ Left as a raw Mantine <Button>: §2's button box is judged on "is there a
 * library equivalent", and there is no Flexi send/email button in
 * @flexinet/ui-components 2.0.2. It carries an icon AND a label, so the icon is
 * not the sole carrier of meaning (§2 Icons).
 *
 * ⚠️ Send is NOT behind a confirmation. Unlike delete it is additive and
 * recoverable — a resend is harmless — so the confirmation dialog would be
 * friction without a decision behind it. `isPending` disables it against a
 * double-send.
 */
export const SendRecordButton = ({
                                     selectedIds,
                                     onSendComplete,
                                 }: SendRecordButtonProps) => {

    const sendMutation = useSendInvoicesMutation();

    return (
        <Button
            disabled={selectedIds.length === 0 || sendMutation.isPending}
            leftSection={<AiOutlineMail/>}
            onClick={async () => {
                if (selectedIds.length === 0) {
                    return;
                }
                await sendMutation.mutateAsync(selectedIds);
                onSendComplete?.();
            }}
        >
            {I18n.get('Send')}
        </Button>
    );
};
